import type { DelayActionUI, TimerUI } from './DeviceActionUI';

export interface NotifyAction {
	name: string;
	value: string | number;
}

export interface NotificationActionUI {
	id: number;
	name: string;
	msg: string;
	msg_type: 'email' | 'push' | 'alarm' | 'event_log' | 'alexa_notif' | 'alexa_notif_premium';
	type: 'mail_notification' | 'push_notification' | 'event_log' | 'alexa_notif';
	delay?: DelayActionUI | undefined;
	timer?: TimerUI;
	/**
	 * FE-1682: Optional list of sub-unit ids (shared home members) that should
	 * receive this notification action.
	 *
	 * - Only meaningful for `push_notification` and `mail_notification`.
	 * - The home owner is **not** represented here — owner delivery for this
	 *   action is governed by the sibling `pushSkipUnit` flag.
	 * - `undefined` (legacy) → all current sub-units of the home receive it.
	 * - `[]` → no sub-units receive it (owner-only delivery, assuming
	 *   `pushSkipUnit` is not `true`).
	 * - `[1, 3, ...]` → only the listed sub-unit ids receive it.
	 */
	toSub?: number[];
	/**
	 * FE-1682: opt-out for the home owner on **this** notification action.
	 * `true` → owner does not receive it. Missing/`false` → owner receives it
	 * (assuming the action is a push/mail notification type that supports
	 * per-recipient delivery).
	 */
	pushSkipUnit?: boolean;
}

export function is_notification_action_ui(obj: any): obj is NotificationActionUI {
	return (
		typeof obj === 'object' &&
		typeof obj.id === 'number' &&
		typeof obj.type !== 'undefined' &&
		typeof obj.msg === 'string'
	);
}

/**
 * Notification types that support per-recipient delivery on shared homes.
 *
 * Excluded:
 * - `event_log` — shared activity log visible to all members.
 * - `alexa_notif` — fires an account-level event into Amazon Alexa.
 */
export const NOTIFICATION_TYPES_WITH_RECIPIENTS: ReadonlyArray<NotificationActionUI['type']> = [
	'push_notification',
	'mail_notification',
];

export function notificationTypeSupportsRecipients(
	type: NotificationActionUI['type'] | string | undefined,
): boolean {
	return NOTIFICATION_TYPES_WITH_RECIPIENTS.includes(type as NotificationActionUI['type']);
}
